import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { GoogleMap, useLoadScript, InfoWindow } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

interface Company {
  id: number;
  company_name: string;
  industry: string;
  employee_count: number;
  description: string;
  formatted_address: string;
  latitude: number;
  longitude: number;
  locality: string;
  country: string;
  pin_color: string;
  logo_url: string;
  website: string;
  domain: string;
  founded: string;
  type: string;
}

interface PinGroup {
  color: string;
  name: string | null;
  is_visible: boolean;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 }; // London

const EmbedView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();

  // Settings from URL params
  const showLegend = searchParams.get('legend') === '1';
  const showSearch = searchParams.get('search') === '1';

  const [projectName, setProjectName] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pinGroups, setPinGroups] = useState<PinGroup[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [visibleColors, setVisibleColors] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(4);

  const mapRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const companiesMapRef = useRef<Map<google.maps.Marker, Company>>(new Map());

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
  });

  // Fetch project data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch project info
        const projectResponse = await fetch(`/api/embed/${projectId}`);
        if (!projectResponse.ok) {
          const errorData = await projectResponse.json();
          throw new Error(errorData.error || 'Failed to load project');
        }
        const projectData = await projectResponse.json();
        setProjectName(projectData.name);

        // Fetch companies
        const companiesResponse = await fetch(`/api/embed/${projectId}/companies`);
        if (companiesResponse.ok) {
          const companiesData = await companiesResponse.json();
          console.log('Embed: Fetched companies:', companiesData.length);

          // Normalize pin_color - treat null as default blue
          const normalizedCompanies = companiesData.map((c: Company) => ({
            ...c,
            pin_color: c.pin_color || '#3B82F6'
          }));
          setCompanies(normalizedCompanies);

          // Calculate center and bounds from companies
          if (normalizedCompanies.length > 0) {
            const validCompanies = normalizedCompanies.filter((c: Company) =>
              c.latitude && c.longitude && !isNaN(c.latitude) && !isNaN(c.longitude)
            );

            if (validCompanies.length > 0) {
              const avgLat = validCompanies.reduce((sum: number, c: Company) => sum + c.latitude, 0) / validCompanies.length;
              const avgLng = validCompanies.reduce((sum: number, c: Company) => sum + c.longitude, 0) / validCompanies.length;
              setMapCenter({ lat: avgLat, lng: avgLng });
            }
          }

          // Extract unique colors and set all visible
          const colors = new Set<string>(normalizedCompanies.map((c: Company) => c.pin_color));
          setVisibleColors(colors);
        }

        // Fetch pin groups (don't fail if this errors)
        try {
          const pinGroupsResponse = await fetch(`/api/embed/${projectId}/pin-groups`);
          if (pinGroupsResponse.ok) {
            const pinGroupsData = await pinGroupsResponse.json();
            setPinGroups(pinGroupsData);
          }
        } catch (e) {
          console.log('Pin groups fetch failed, continuing without legend');
        }

        setLoading(false);
      } catch (err: any) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  // Filter companies
  const filteredCompanies = React.useMemo(() => {
    return companies.filter(company => {
      if (!company.latitude || !company.longitude) return false;
      if (!visibleColors.has(company.pin_color)) return false;

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          company.company_name?.toLowerCase().includes(term) ||
          company.industry?.toLowerCase().includes(term) ||
          company.locality?.toLowerCase().includes(term) ||
          company.country?.toLowerCase().includes(term)
        );
      }

      return true;
    });
  }, [companies, visibleColors, searchTerm]);

  // Create markers and clustering when map loads or data changes
  useEffect(() => {
    if (!mapRef.current || !isLoaded || filteredCompanies.length === 0) return;

    // Clear existing markers and clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    companiesMapRef.current.clear();

    // Create markers for filtered companies
    const markers = filteredCompanies.map(company => {
      const marker = new google.maps.Marker({
        position: { lat: company.latitude, lng: company.longitude },
        title: company.company_name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: company.pin_color || '#3B82F6',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });

      marker.addListener('click', () => {
        setSelectedCompany(company);
      });

      companiesMapRef.current.set(marker, company);
      return marker;
    });

    markersRef.current = markers;

    // Create or update clusterer
    if (clustererRef.current) {
      clustererRef.current.addMarkers(markers);
    } else {
      clustererRef.current = new MarkerClusterer({
        map: mapRef.current,
        markers,
        renderer: {
          render: ({ count, position }) => {
            return new google.maps.Marker({
              position,
              label: {
                text: String(count),
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 'bold'
              },
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 20 + Math.min(count / 100, 10),
                fillColor: '#3B82F6',
                fillOpacity: 0.9,
                strokeColor: '#ffffff',
                strokeWeight: 3
              },
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
            });
          }
        }
      });
    }

    // Fit bounds to show all markers
    if (markers.length > 0 && mapRef.current) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(marker => {
        const pos = marker.getPosition();
        if (pos) bounds.extend(pos);
      });
      mapRef.current.fitBounds(bounds, 50);
    }

    return () => {
      // Cleanup on unmount
      markersRef.current.forEach(marker => {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
    };
  }, [filteredCompanies, isLoaded]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const toggleColorVisibility = (color: string) => {
    setVisibleColors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(color)) {
        newSet.delete(color);
      } else {
        newSet.add(color);
      }
      return newSet;
    });
  };

  if (loadError) {
    return <div className="flex items-center justify-center h-screen bg-gray-100">
      <p className="text-red-500">Error loading maps</p>
    </div>;
  }

  if (loading || !isLoaded) {
    return <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="text-center">
        <p className="text-red-500 text-lg mb-2">{error}</p>
        <p className="text-gray-500 text-sm">This project may not be available for embedding.</p>
      </div>
    </div>;
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-100">
      {/* Header with search */}
      {showSearch && (
        <div className="p-2 bg-white shadow-sm">
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Map container */}
      <div className="flex-1 relative">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true
          }}
        >
          {selectedCompany && (
            <InfoWindow
              position={{ lat: selectedCompany.latitude, lng: selectedCompany.longitude }}
              onCloseClick={() => setSelectedCompany(null)}
            >
              <div className="p-2 max-w-xs">
                <div className="flex items-center gap-2 mb-2">
                  {selectedCompany.logo_url && (
                    <img
                      src={selectedCompany.logo_url}
                      alt={selectedCompany.company_name}
                      className="w-8 h-8 rounded object-contain"
                    />
                  )}
                  <h3 className="font-semibold text-gray-900">{selectedCompany.company_name}</h3>
                </div>
                {selectedCompany.industry && (
                  <p className="text-sm text-gray-600 mb-1">{selectedCompany.industry}</p>
                )}
                {selectedCompany.formatted_address && (
                  <p className="text-sm text-gray-500">{selectedCompany.formatted_address}</p>
                )}
                {selectedCompany.employee_count && (
                  <p className="text-xs text-gray-400 mt-1">
                    {selectedCompany.employee_count.toLocaleString()} employees
                  </p>
                )}
                {selectedCompany.website && (
                  <a
                    href={selectedCompany.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline block mt-2"
                  >
                    Visit website
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Legend */}
        {showLegend && pinGroups.length > 0 && (
          <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 max-w-xs">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Legend</h4>
            <div className="space-y-1">
              {pinGroups.map((group) => (
                <label
                  key={group.color}
                  className="flex items-center gap-2 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={visibleColors.has(group.color)}
                    onChange={() => toggleColorVisibility(group.color)}
                    className="rounded"
                  />
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                  <span className="text-gray-600">{group.name || 'Unnamed'}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Company count */}
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow px-3 py-1">
          <span className="text-sm text-gray-600">
            {filteredCompanies.length.toLocaleString()} {filteredCompanies.length === 1 ? 'location' : 'locations'}
          </span>
        </div>
      </div>

      {/* Footer attribution */}
      <div className="p-1 bg-gray-50 text-center border-t">
        <span className="text-xs text-gray-400">
          Powered by <a href="https://www.marketmapr.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">MarketMapr</a>
        </span>
      </div>
    </div>
  );
};

export default EmbedView;
